/**
 * InviteAcceptPage — Public page for accepting guest invitations.
 * Validates the invite token. If authenticated, shows accept button.
 * If not authenticated, embeds signup form inline (single-page flow).
 */

import { useState, useEffect, type FormEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container, Card, Alert, Spinner, Button, Form } from "react-bootstrap";
import { signUp, confirmSignUp, signIn } from "aws-amplify/auth";
import config from "../config";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { isNepEmail } from "../authMode";

interface InviteDetails {
  inviteId: string;
  email: string;
  businessUnitId: string;
  streamIds: string[];
  expiresAt?: number;
}

type SignupStep = "form" | "verify" | "complete";

export default function InviteAcceptPage() {
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Invite state
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Inline signup state
  const [signupStep, setSignupStep] = useState<SignupStep>("form");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [signupError, setSignupError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const validateInvite = async () => {
      if (!inviteId) {
        setError("Invalid invitation link");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${config.apiGateway.URL}/api/guests/accept/${inviteId}`);
        const data = await response.json();

        if (!response.ok) {
          setError(data.error || "This invitation is no longer valid");
        } else {
          setInvite(data.invite);
        }
      } catch {
        setError("Failed to validate invitation. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    validateInvite();
  }, [inviteId]);

  // Auto-accept when user becomes authenticated (after inline signup completes)
  useEffect(() => {
    if (isAuthenticated && invite && !accepted && signupStep === "complete") {
      handleAccept();
    }
  }, [isAuthenticated, invite, signupStep, accepted]);

  const handleAccept = async () => {
    if (!isAuthenticated) return;
    setAccepting(true);
    try {
      await api.post(`/api/guests/accept/${inviteId}`);
      setAccepted(true);
      setTimeout(() => navigate("/"), 1500);
    } catch (err: any) {
      setError(err.body?.error || err.message || "Failed to accept invitation");
    } finally {
      setAccepting(false);
    }
  };

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setSignupError(null);

    if (!invite) return;

    if (isNepEmail(invite.email)) {
      setSignupError("NEP Group users (@nepgroup.com) must use Okta SSO. Please go to the login page.");
      return;
    }

    if (password !== confirmPassword) {
      setSignupError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setSignupError("Password must be at least 8 characters.");
      return;
    }

    setIsSubmitting(true);
    try {
      await signUp({
        username: invite.email.toLowerCase(),
        password,
        options: {
          userAttributes: {
            email: invite.email.toLowerCase(),
            given_name: firstName.trim(),
            family_name: lastName.trim(),
          },
        },
      });
      setSignupStep("verify");
    } catch (err: any) {
      if (err.name === "UsernameExistsException") {
        setSignupError("An account with this email already exists. Please sign in instead.");
      } else {
        setSignupError(err.message || "Sign up failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setSignupError(null);
    setIsSubmitting(true);

    try {
      if (!invite) return;

      await confirmSignUp({
        username: invite.email.toLowerCase(),
        confirmationCode: verificationCode,
      });

      // Auto-login after verification
      await signIn({
        username: invite.email.toLowerCase(),
        password,
      });

      setSignupStep("complete");

      // Directly call accept API since we just signed in (auth token is now available)
      try {
        await api.post(`/api/guests/accept/${inviteId}`);
        setAccepted(true);
        setTimeout(() => navigate("/"), 1500);
      } catch (acceptErr: any) {
        // If direct accept fails (e.g., token not yet propagated), 
        // the useEffect will retry once isAuthenticated updates
        console.warn("Direct accept after signup failed, will retry via effect:", acceptErr);
      }
    } catch (err: any) {
      if (err.name === "CodeMismatchException") {
        setSignupError("Invalid verification code. Please check your email and try again.");
      } else if (err.name === "ExpiredCodeException") {
        setSignupError("Verification code expired. Please request a new one.");
      } else {
        setSignupError(err.message || "Verification failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
      <Card style={{ width: "100%", maxWidth: "480px" }}>
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <img
              src="/nep-logo.png"
              alt="NEP Group"
              className="mb-3"
              style={{ maxWidth: "160px", height: "auto" }}
            />
            <h2 className="fw-bold" style={{ color: "#0e67f5" }}>AnyPlay</h2>
            <p className="text-muted">Guest Invitation</p>
          </div>

          {error && (
            <Alert variant="danger">
              <Alert.Heading>Invitation Error</Alert.Heading>
              <p className="mb-0">{error}</p>
            </Alert>
          )}

          {accepted && (
            <Alert variant="success">
              <p className="mb-0">Invitation accepted! Redirecting to your dashboard...</p>
            </Alert>
          )}

          {invite && !accepted && (
            <>
              {/* Invite summary — always shown */}
              <Alert variant="success" className="mb-3">
                <Alert.Heading className="h6">You're Invited!</Alert.Heading>
                <p className="mb-1">
                  You've been invited to view <strong>{invite.streamIds.length} stream(s)</strong> on AnyPlay.
                </p>
                {invite.expiresAt && (
                  <small className="text-muted">
                    Expires: {new Date(invite.expiresAt * 1000).toLocaleDateString()}
                  </small>
                )}
              </Alert>

              {/* Authenticated user — just accept */}
              {isAuthenticated ? (
                <>
                  <p className="text-muted" style={{ fontSize: "0.85rem" }}>
                    You're signed in as <strong>{user?.email}</strong>. Click below to accept.
                  </p>
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={handleAccept}
                    disabled={accepting}
                  >
                    {accepting ? (
                      <><Spinner animation="border" size="sm" className="me-2" /> Accepting...</>
                    ) : (
                      "Accept Invitation"
                    )}
                  </Button>
                </>
              ) : (
                <>
                  {/* NEP Group users must use Okta SSO — don't show Cognito signup form */}
                  {isNepEmail(invite.email) ? (
                    <>
                      <Alert variant="info">
                        <strong>@nepgroup.com</strong> users sign in with Okta SSO.
                        <br />
                        Your account is created automatically on first sign-in.
                      </Alert>
                      <Button
                        variant="primary"
                        className="w-100 mb-2"
                        onClick={() => navigate("/login", { state: { from: { pathname: `/invite/${inviteId}` } } })}
                      >
                        Sign in with Okta SSO
                      </Button>
                      <div className="text-center">
                        <small className="text-muted">
                          After signing in, you'll be brought back to accept this invitation.
                        </small>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Inline signup form for external (non-NEP) users */}
                      {signupError && <Alert variant="danger">{signupError}</Alert>}

                      {signupStep === "form" && (
                    <>
                      <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
                        Create your account to get started.
                      </p>

                      <Form onSubmit={handleSignUp}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            value={invite.email}
                            disabled
                          />
                          <Form.Text className="text-muted">
                            Pre-filled from your invitation.
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>First Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="First name"
                            required
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Last name"
                            required
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Password</Form.Label>
                          <Form.Control
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="At least 8 characters"
                            required
                            minLength={8}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Confirm Password</Form.Label>
                          <Form.Control
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Re-enter password"
                            required
                          />
                        </Form.Group>

                        <Button
                          variant="primary"
                          type="submit"
                          className="w-100 mb-3"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <><Spinner animation="border" size="sm" className="me-2" /> Creating Account...</>
                          ) : (
                            "Create Account & Accept"
                          )}
                        </Button>

                        <div className="text-center">
                          <small className="text-muted">
                            Already have an account?{" "}
                            <Link to="/login">Sign In</Link>
                          </small>
                        </div>
                      </Form>
                    </>
                  )}

                  {signupStep === "verify" && (
                    <Form onSubmit={handleVerify}>
                      <Alert variant="info">
                        We've sent a verification code to <strong>{invite.email}</strong>. Check your inbox (and spam folder).
                      </Alert>

                      <Form.Group className="mb-3">
                        <Form.Label>Verification Code</Form.Label>
                        <Form.Control
                          type="text"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          placeholder="Enter 6-digit code"
                          required
                          maxLength={6}
                          autoFocus
                        />
                      </Form.Group>

                      <Button
                        variant="primary"
                        type="submit"
                        className="w-100 mb-3"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <><Spinner animation="border" size="sm" className="me-2" /> Verifying...</>
                        ) : (
                          "Verify & Accept Invitation"
                        )}
                      </Button>

                      <div className="text-center">
                        <small className="text-muted">
                          Didn't receive a code?{" "}
                          <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none"
                            style={{ fontSize: "inherit" }}
                            onClick={() => { setSignupStep("form"); setSignupError(null); }}
                          >
                            Try again
                          </button>
                        </small>
                      </div>
                    </Form>
                  )}

                  {signupStep === "complete" && (
                    <div className="text-center">
                      <Spinner animation="border" variant="primary" className="mb-3" />
                      <p className="text-muted">Accepting your invitation...</p>
                    </div>
                  )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
